import { ROAD_PROVIDERS } from "~/lib/road-providers";

// Every branch starts with the same two screens: pick the brand, then pick the
// backoffice its sessions live in. A charger is never locked to one backoffice,
// so even brands with their own cloud pass through connect-method — their own
// backoffice is simply the preferred option there.
export const CHOOSE_API_STEPS = [
  "provider-selection",
  "connect-method",
  "eligibility-check",
  "register",
  "connect-account",
  "link-charger",
  "finish",
];

export const CHOOSE_APIKEY_STEPS = [
  "provider-selection",
  "connect-method",
  "eligibility-check",
  "register",
  "connect-account-apikey",
  "link-charger",
  "finish",
];

// Peblar authenticates with a charger ID + PIN, so its backoffice (Peblar
// Connect Hub) has its own connect step.
export const CHOOSE_PEBLAR_STEPS = [
  "provider-selection",
  "connect-method",
  "eligibility-check",
  "register",
  "connect-account-peblar",
  "link-charger",
  "finish",
];

// Blue Current's own backoffice authenticates with email + password, so it
// reuses the shared credentials screen via its own connect step.
export const CHOOSE_BLUECURRENT_STEPS = [
  "provider-selection",
  "connect-method",
  "eligibility-check",
  "register",
  "connect-account-bluecurrent",
  "link-charger",
  "finish",
];

export const CHOOSE_OCPP_STEPS = [
  "provider-selection",
  "connect-method",
  "provider-type",
  "session-method",
  "eligibility-check",
  "register",
  "connect-account-ocpp",
  "link-charger-ocpp",
  "finish",
];

export const CHOOSE_CREDENTIALS_STEPS = [
  "provider-selection",
  "connect-method",
  "eligibility-check",
  "register",
  "connect-account-credentials",
  "link-charger",
  "finish",
];

export const CHOOSE_MANUAL_STEPS = [
  "provider-selection",
  "connect-method",
  "provider-type",
  "session-method",
  "eligibility-check",
  "register",
  "connect-account-manual",
  "finish",
];

export const UNSUPPORTED_STEPS = [
  "provider-selection",
  "connect-method",
  "provider-type",
  "unsupported-charger",
];

export const BRANCH_FLOWS = {
  api: CHOOSE_API_STEPS,
  apikey: CHOOSE_APIKEY_STEPS,
  peblar: CHOOSE_PEBLAR_STEPS,
  bluecurrent: CHOOSE_BLUECURRENT_STEPS,
  ocpp: CHOOSE_OCPP_STEPS,
  credentials: CHOOSE_CREDENTIALS_STEPS,
  manual: CHOOSE_MANUAL_STEPS,
  unsupported: UNSUPPORTED_STEPS,
} as const;

export type Branch = keyof typeof BRANCH_FLOWS;

// `via` is the id of the ConnectOption the user picked on connect-method (see
// lib/connect-options.ts). Manufacturer backoffices whose id equals their
// brandId are only listed here when they need a branch other than "api"; the
// rest resolve through the capability check below, which reads the same flags.
const VIA_TO_BRANCH: Record<string, Branch> = {
  eflux: "credentials",
  eneco: "credentials",
  fiftyfive: "credentials",
  ecotap: "credentials",
  evcompany: "credentials",
  maxem: "credentials",
  "monta-public": "credentials",
  plugchoice: "api",
  ...Object.fromEntries(ROAD_PROVIDERS.map(({ id }) => [id, "api"] as const)),
  v2c: "apikey",
  ltssolar: "apikey",
  peblar: "peblar",
  bluecurrent: "bluecurrent",
  ocpp: "ocpp",
  manual: "manual",
};

export function resolveBranch(
  pathname: string,
  provider?: { hasOAuth?: boolean; hasROPC?: boolean; hasApiKey?: boolean } | null,
  via?: string,
): Branch {
  if (pathname.endsWith("unsupported-charger")) return "unsupported";
  if (pathname.endsWith("-ocpp")) return "ocpp";
  if (pathname.endsWith("-credentials")) return "credentials";
  if (pathname.endsWith("-apikey")) return "apikey";
  if (pathname.endsWith("-peblar")) return "peblar";
  if (pathname.endsWith("-bluecurrent")) return "bluecurrent";
  if (pathname.endsWith("-manual")) return "manual";
  // On pre-branch screens the URL doesn't carry a suffix yet, but if the user
  // has already picked a connect method, `via` tells us which flow we're on.
  if (via && VIA_TO_BRANCH[via]) return VIA_TO_BRANCH[via];
  // These two screens only exist on the manual-entry branches. A brand with its
  // own backoffice can still end up here by choosing "ergens anders", and then
  // it belongs to the manual flow, not the API flow its capabilities imply.
  if (pathname.endsWith("provider-type") || pathname.endsWith("session-method")) {
    return "manual";
  }
  // Unsuffixed leaves (`connect-account`, `link-charger`) belong to the API
  // flow, as do pre-branch screens for OAuth/ROPC providers.
  if (provider?.hasOAuth || provider?.hasROPC) return "api";
  // API-key providers (e.g. V2C) share the API flow's unsuffixed leaves
  // (`link-charger`, `finish`) and pre-branch screens, so resolve them here.
  if (provider?.hasApiKey) return "apikey";
  // Pre-branch screens for non-API providers default to manual; the chooser
  // branches for non-API paths share the same prefix so the counter holds.
  return "manual";
}

// Connect screen per ConnectOption id. Manufacturer backoffices use their
// brandId as the id, so e.g. `via=peblar` lands on the Peblar Connect Hub form
// while `via=eflux` for the same charger lands on the shared credentials form.
// Ids absent here (Tesla, Easee, Zaptec, …) use the OAuth connect-account.
const CONNECT_ACCOUNT_STEPS: Record<string, string> = {
  eflux: "connect-account-credentials",
  eneco: "connect-account-credentials",
  fiftyfive: "connect-account-credentials",
  ecotap: "connect-account-credentials",
  evcompany: "connect-account-credentials",
  maxem: "connect-account-credentials",
  "monta-public": "connect-account-credentials",
  plugchoice: "connect-account",
  ...Object.fromEntries(ROAD_PROVIDERS.map(({ id }) => [id, "connect-account"])),
  v2c: "connect-account-apikey",
  ltssolar: "connect-account-apikey",
  peblar: "connect-account-peblar",
  bluecurrent: "connect-account-bluecurrent",
  ocpp: "connect-account-ocpp",
  manual: "connect-account-manual",
};

// Brands that used to reach their connect screen without a `via`, back when
// picking the brand routed straight through. Kept so onboarding links minted
// before the chooser became mandatory still land on the right screen.
const LEGACY_PROVIDER_STEPS: Record<string, string> = {
  v2c: "connect-account-apikey",
  peblar: "connect-account-peblar",
  bluecurrent: "connect-account-bluecurrent",
};

export const connectAccountStep = (via?: string, provider?: string) => {
  if (via && CONNECT_ACCOUNT_STEPS[via]) return CONNECT_ACCOUNT_STEPS[via];
  if (!via && provider && LEGACY_PROVIDER_STEPS[provider]) {
    return LEGACY_PROVIDER_STEPS[provider];
  }
  return "connect-account";
};
